import "@/App.css";
import { useContext, useState } from "react";
import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { User } from "lucide-react";
import Home from "@/pages/Home";
import Journey from "@/pages/Journey";
import CommunityWisdom from "@/pages/CommunityWisdom";
import Subscription from "@/pages/Subscription";
import ResetPassword from "@/pages/ResetPassword";
import PractitionerDashboard from "@/pages/PractitionerDashboard";
import LegalScroll from "@/components/LegalScroll";
import ShareSheet from "@/components/ShareSheet";
import DecayNotification from "@/components/DecayNotification";
import AuthModal from "@/components/AuthModal";
import ProfileDropdown from "@/components/ProfileDropdown";
import { EnlightenmentProvider, EnlightenmentContext } from "@/context/EnlightenmentContext";
import { AuthProvider, AuthContext } from "@/context/AuthContext";
import PapayaChat from "@/components/PapayaChat";
import GardenGate from "@/components/GardenGate";
import SubscriptionModal from "@/components/SubscriptionModal";
import LotusPaywall from "@/components/LotusPaywall";
import QAConsole from "@/components/QAConsole";
import ErrorBoundary from "@/components/ErrorBoundary";

const TopBar = () => {
  const { user } = useContext(AuthContext);
  const { sessionId } = useContext(EnlightenmentContext);
  const [showAuth, setShowAuth] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [shareSheetOpen, setShareSheetOpen] = useState(false);

  return (
    <>
      {/* Fixed Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-[100] flex items-center justify-between px-4 py-2" style={{ backgroundColor: 'rgba(248,245,240,0.9)', backdropFilter: 'blur(8px)' }}>
        {/* Discord - Left */}
        <a href="https://discord.gg/RGQPQ4CJGk" target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors hover:opacity-80"
          style={{ backgroundColor: '#5865F2', color: '#FFF' }}
          data-testid="discord-link"
        >
          <svg width="16" height="12" viewBox="0 0 71 55" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M60.1 4.9A58.5 58.5 0 0045.4.2a.2.2 0 00-.2.1 40.8 40.8 0 00-1.8 3.7 54 54 0 00-16.2 0A37.4 37.4 0 0025.4.3a.2.2 0 00-.2-.1A58.4 58.4 0 0010.5 4.9a.2.2 0 00-.1.1C1.5 18.7-.9 32.2.3 45.5v.1a58.8 58.8 0 0017.7 9 .2.2 0 00.3-.1 42.1 42.1 0 003.6-5.9.2.2 0 00-.1-.3 38.8 38.8 0 01-5.5-2.6.2.2 0 01 0-.4l1.1-.9a.2.2 0 01.2 0 42 42 0 0035.6 0 .2.2 0 01.2 0l1.1.9a.2.2 0 01 0 .4 36.4 36.4 0 01-5.5 2.6.2.2 0 00-.1.3 47.3 47.3 0 003.6 5.9.2.2 0 00.3.1A58.6 58.6 0 0070.5 45.6v-.1c1.4-15-2.3-28-9.8-39.6a.2.2 0 00-.1-.1zM23.7 37.3c-3.4 0-6.3-3.2-6.3-7s2.8-7 6.3-7 6.4 3.2 6.3 7-2.8 7-6.3 7zm23.2 0c-3.4 0-6.3-3.2-6.3-7s2.8-7 6.3-7 6.4 3.2 6.3 7-2.8 7-6.3 7z"/>
          </svg>
          <span className="hidden sm:inline">Discord</span>
        </a>

        {/* User - Right */}
        {user ? (
          <div className="relative">
            <button onClick={() => setShowProfile(v => !v)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
              style={{ backgroundColor: 'rgba(79,55,139,0.1)', color: '#4F378B' }}
              data-testid="user-menu-button"
            >
              {user.profile_pic ? (
                <img src={user.profile_pic} alt="" className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <User size={14} />
              )}
              <span className="hidden sm:inline">{user.name || user.email}</span>
            </button>
            {showProfile && (
              <ProfileDropdown
                onClose={() => setShowProfile(false)}
                onOpenSubscription={() => setShowSubscriptionModal(true)}
                onOpenShareSheet={() => setShareSheetOpen(true)}
              />
            )}
          </div>
        ) : (
          <button onClick={() => setShowAuth(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ backgroundColor: 'rgba(79,55,139,0.1)', color: '#4F378B' }}
            data-testid="sign-in-button"
          >
            <User size={14} />
            <span>Sign In</span>
          </button>
        )}
      </div>

      {/* Subscription Modal */}
      <SubscriptionModal open={showSubscriptionModal} onClose={() => setShowSubscriptionModal(false)} />

      {/* Auth Modal — extracted to its own component (iter-43) */}
      <AuthModal
        open={showAuth}
        onClose={() => setShowAuth(false)}
        sessionId={sessionId}
      />
      <ShareSheet
        open={shareSheetOpen}
        onClose={() => setShareSheetOpen(false)}
        refCode={user?.referral_code || user?.email || ''}
        origin={typeof window !== 'undefined' ? window.location.origin : ''}
      />
    </>
  );
};


const AppContent = () => {
  // Background swaps by band: Botaneia (jungle) → Anamalia (animal print) at tier 10 → Noctyra (stars) at tier 27
  const { currentTier, paywallOpen, closePaywall } = useContext(EnlightenmentContext);
  let bgClass = 'spirit-bg';
  let bgTestId = 'app-bg-botaneia';
  if (currentTier >= 27) {
    bgClass = 'spirit-bg spirit-bg-noctyra';
    bgTestId = 'app-bg-noctyra';
  } else if (currentTier >= 10) {
    bgClass = 'spirit-bg spirit-bg-anamalia';
    bgTestId = 'app-bg-anamalia';
  }
  return (
    <div className={`App ${bgClass} pt-10`} data-testid={bgTestId}>
      <TopBar />
      <DecayNotification />
      <GardenGate />
      <LegalScroll />
      <PapayaChat />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/journey" element={<Journey />} />
        <Route path="/community" element={<CommunityWisdom />} />
        <Route path="/subscription" element={<Subscription />} />
        <Route path="/practitioner" element={<PractitionerDashboard />} />
        <Route path="/reset-password" element={<ResetPassword />} />
      </Routes>
      <LotusPaywall open={paywallOpen} onClose={closePaywall} />
      <QAConsole />
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <EnlightenmentProvider>
            <AppContent />
          </EnlightenmentProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
